<template>
  <div class="pc-article">
    <div class="pc-article__body">
      <h3>{{ $t('configurator.headings.booth_package') }}</h3>
    </div>
    <div class="pc-article__parent">
      <div>
        <img :src="previewImage" alt="" class="pc-article__image">
      </div>
      <div class="pc-article__body">
        <span class="pc-article__title">{{ name }}</span>
        <article class="text-break">{{ description }}</article>
      </div>
    </div>
    <div class="pc-article__body">
      <hr>
      <h4 class="border--b-0">
        {{ $t('configurator.headings.graphic_design') }} <span class="text-sm">({{ $t('configurator.headings.graphic_design_deadline') }})</span>
      </h4>
      <table class="table mb-1">
        <thead>
          <th style="min-width: 140px; width: auto;">{{ $t('configurator.tables.columns.surface') }}</th>
          <th>{{ $t('configurator.tables.columns.print_article') }}</th>
          <th style="min-width: 80px; width: auto;">{{ $t('configurator.tables.columns.price') }}</th>
          <th>{{ $t('configurator.tables.columns.lettering_graphic_file') }}</th>
          <th></th>
        </thead>
        <tbody>
          <template v-for="group,gIndex in groupedOrderedRefinements" :key="gIndex">
            <print-cloud-booth-refinement-group v-if="group.length > 1"
              :group="group"
              @download="download"
              @show="showArticleModal">
            </print-cloud-booth-refinement-group>
            <print-cloud-booth-refinement v-else
              :refinement="group[0]"
              @download="download"
              @show="showArticleModal">
            </print-cloud-booth-refinement>
          </template>
          <tr v-if="basketConfigurations.length > 0">
            <td colspan="5" style="font-weight: 400; padding-top: 32px">
              {{ $t('configurator.labels.in_basket') }}
            </td>
          </tr>
          <template v-for="group,gIndex in groupedConfigurations" :key="gIndex">
            <print-cloud-basket-booth-refinement-group v-if="group.length > 1"
              :group="group"
              @download="download"
              @remove="removeConfiguration"
              @show="showArticleModal">
            </print-cloud-basket-booth-refinement-group>
            <print-cloud-basket-booth-refinement v-else
              :configuration="group[0]"
              @download="download"
              @remove="removeConfiguration"
              @show="showArticleModal">
            </print-cloud-basket-booth-refinement>
          </template>
          <tr v-if="availableRefinements.length > 0 && isCloudStatusOpen && !isShopClosed">
            <td colspan="5">
              <span class="text-primary cursor--pointer" @click="showAddModal">
                <i class="fa fa-plus mr-1"></i> {{ $t('configurator.buttons.add_graphic_design') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="pc-article__body">
      <hr class="mt-1">
      <h4 class="border--b-0">{{ $t('configurator.headings.booth_draft_optional') }} <span class="text-sm">({{ $t('configurator.headings.booth_draft_required') }})</span></h4>
      <table class="table">
        <thead>
          <th>{{ $t('configurator.tables.columns.file') }}</th>
          <th></th>
        </thead>
        <tbody v-if="cloud.status < PRINT_CLOUD_STATUS_FINISHED">
          <tr v-if="boothDraft">
            <td>
              <span
                class="text-primary cursor--pointer"
                @click="downloadBoothDraft">
                <i class="fa fa-download"></i> {{ boothDraft.original_name }}
              </span>
            </td>
            <td>
              <span v-if="isCloudStatusOpen"
                class="text-primary cursor--pointer"
                @click="confirmRemoveDraft">
                <i class="fa fa-trash"></i> {{ $t('configurator.buttons.delete') }}
              </span>
            </td>
          </tr>
          <tr v-else>
            <td colspan="2">
              <i>{{ $t('configurator.labels.no_booth_draft_uploaded') }}</i>
              <span v-if="isCloudStatusOpen"
                class="text-primary cursor--pointer"
                @click="showUploadModal">
                <i class="fa fa-upload mr-1"></i> {{ $t('configurator.buttons.upload_now') }}
              </span>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-if="boothDraft">
            <td colspan="2">
              <span class="text-primary">{{ boothDraft.original_name }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <article-modal v-if="articleModalActive"
    :article="selectedArticle"
    @close="hideArticleModal">
  </article-modal>

  <AddBoothRefinementModal v-if="addModalActive"
    :booth="booth"
    :booth-type="boothType"
    :form="refinementForm"
    :available-refinements="availableRefinements"
    :width="basket.width"
    :depth="basket.depth"
    @close="hideAddModal"
    @submit="addConfigurations" />

  <upload-booth-draft-modal v-if="uploadModalActive"
    @close="hideUploadModal"
    @uploaded="onUploaded">
  </upload-booth-draft-modal>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useMainStore } from '@/store'
import { useCloudStore } from '@/stores/cloud'
import { ARTICLE_DEFAULT_IMAGE, PRINT_CLOUD_STATUS_FINISHED, PRINT_CLOUD_STATUS_OPEN, UNIT_PC } from '@/constants'
import { downloadFile } from '@/utils/file'
import Form from '@/classes/Form'
import { useI18n } from 'vue-i18n'
import { showErrorToast, showLoadingIndicator, showSuccessToast, showWarningToast } from '@/utils/toast'
import Swal from 'sweetalert2'
import moment from 'moment'
import { groupRefinements, isRefinementAvailable } from '@/utils/boothRefinements'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
const cloudStore = useCloudStore()
// define props
const props = defineProps({
  booth: {
    type: Object,
    required: true
  },
  boothType: {
    type: String,
    required: true
  }
})
// define reactive props
const addModalActive = ref(false)
const articleModalActive = ref(false)
const uploadModalActive = ref(false)
const selectedArticle = ref(null)
// const dropzoneFile = ref('')
const refinementForm = reactive(new Form({
  refinement: null, // deprecated
  refinements: [],
  spanning_mode: null, 
  is_logo_print: false,
  label: '',
  article: null,
  file: null,
  is_seamless_print: false,
}))
// define computed props
const inEnglish = computed(() => store.inEnglish)
const printArticles = computed(() => cloudStore.data.print_articles)
const cloud = computed(() => store.tmp.cloud)
const basket = computed(() => store.print.basket)
const boothArticles = computed(() => store.tmp.booth_articles)
const boothDraft = computed(() => store.tmp.booth_draft)
const isShopClosed = computed(() => store.tmp.is_exhibition_closed)

const disabledBoothRefinementArticleIds = computed(() => store.availability.disabled_booth_refinement_ids)
const bookableArticleIds = computed(() => store.availability.bookable_article_ids)

const isCloudStatusOpen = computed(() => {
  return cloud.value.status === PRINT_CLOUD_STATUS_OPEN
})

const hasImage = computed(() => {
  if (props.booth.images.hasOwnProperty(props.boothType))
  return props.booth.images[props.boothType].length > 0
})

const previewImage = computed(() => {
  if (!hasImage.value) {
    return ARTICLE_DEFAULT_IMAGE
  }

  return props.booth.images[props.boothType][0].imagePath
})

const name = computed(() => {
  return (inEnglish.value) ? props.booth.name_en : props.booth.name
})

const description = computed(() => {
  return (inEnglish.value) ? props.booth.description_en : props.booth.description
})
// TODO: check if method can be deleted and use the store prop instead
const defaultRefinements = computed(() => {
  return props.booth.refinements.filter((refinement) => {
    return refinement.booth_type === props.boothType
  })
})

// TODO: check if method can be deleted and use the store prop instead
const orderedRefinements = computed(() => {
  return printArticles.value.filter((printArticle) => {
    return printArticle.booth_refinement_id !== null
  })
})

const groupedOrderedRefinements = computed(() => {
  if (orderedRefinements.value.length === 0) {
    return []
  }

  return groupRefinements(orderedRefinements.value)
})
// TODO: check if method can be deleted and use the store prop instead
const availableRefinements = computed(() => {
  if (orderedRefinements.value.length === 0 && basketConfigurations.value.length === 0) {
    return defaultRefinements.value.filter((refinement) => {
      return isRefinementAvailable(refinement, disabledBoothRefinementArticleIds.value, bookableArticleIds.value)
    })
  }

  const ids = orderedRefinements.value.map((refinement) => refinement.booth_refinement_id)
  const basketIds = basketConfigurations.value
    .map((configuration) => configuration.id)

  return defaultRefinements.value
    .filter((refinement) => {
      return isRefinementAvailable(refinement, disabledBoothRefinementArticleIds.value, bookableArticleIds.value)
    })
    .filter((refinement) => {
      return (
        (!(ids.includes(refinement.id) || basketIds.includes(refinement.id)))
        && refinement.articles.length > 0
      )
    })
})
// TODO: check if method can be deleted and use the store prop instead
const basketConfigurations = computed(() => {
  return basket.value.booth_configurations
})

const groupedConfigurations = computed(() => {
  return [
    ...basketConfigurations.value
      .reduce((a, b) => a.set(b.group, [...a.get(b.group) || [], b]), new Map())
      .values()
  ]
})
// define methods
const showArticleModal = (article) => {
  selectedArticle.value = article
  articleModalActive.value = true
}

const hideArticleModal = () => {
  articleModalActive.value = false
  selectedArticle.value = null
}

const download = async (upload) => {
  await downloadFile(
    '/print/' + cloud.value.uuid + '/download/' + upload.id,
    upload.original_name
  )
}

const showAddModal = () => {
  addModalActive.value = true
}

const hideAddModal = () => {
  addModalActive.value = false
}

const addConfigurations = async () => {
  let index = 0
  const group = moment().unix()

  let totalArea = 0
  if (refinementForm.is_seamless_print) {
    totalArea = refinementForm.refinements.reduce((a,b) => a + (parseFloat(b.height) * parseFloat(b.width)), 0)
  }

  await refinementForm.refinements.forEach((refinement) => {
    addConfiguration(refinement, refinementForm, index, group, totalArea, refinementForm.refinements.length)
    index++
  })
  
  refinementForm.resetToOriginal()
  hideAddModal()
}

const addConfiguration = (refinement, form, rIndex, potentialGroup, totalArea = 0, refinementsCount = 1) => {
  const clonedRefinement = _.cloneDeep(refinement)
  const group = (form.spanning_mode && form.spanning_mode.value) ? potentialGroup : clonedRefinement.id
  const configuration = {
    id: clonedRefinement.id,
    booth_refinement: clonedRefinement,
    group: group,
    is_logo_print: form.is_logo_print,
    is_seamless_print: form.is_seamless_print,
    article_id: form.article.article.id,
    label: form.label,
    file: form.file
  }
  basket.value.addBoothConfiguration(configuration)

  // find print article
  const article = boothArticles.value.find((boothArticle) => {
    return boothArticle.id === configuration.article_id
  })
  const clonedArticle = _.cloneDeep(article)
  clonedArticle.booth_refinement_id = clonedRefinement.id
  if (configuration.is_logo_print) {
    clonedArticle.count = 1
    clonedArticle.inclusive = clonedArticle.inclusive
  } else {
    if (form.spanning_mode && form.spanning_mode.value && rIndex > 0) {
      clonedArticle.count = 0
      clonedArticle.inclusive = 0
    } else {
      clonedArticle.count = configuration.label
        .trim()
        .replace(/ /g, '')
        .length
      clonedArticle.inclusive = (clonedArticle.count >= store.tmp.characters_inclusive)
        ? store.tmp.characters_inclusive
        : clonedArticle.count
    }
  }

  if(form.is_seamless_print) {
    clonedArticle.is_seamless_print = 1
    clonedArticle.name = clonedArticle.name + ' (nahtloser Druck)'
    clonedArticle.name_en = clonedArticle.name_en + ' (seamless print)'
    if (rIndex === 0) {
      clonedArticle.count = (clonedArticle.unit === UNIT_PC)
        ? refinementsCount
        : totalArea
    } else {
      clonedArticle.count = 0
    }
  }

  if(form.article.is_included) {
    clonedArticle.name = clonedArticle.name + ' (inklusive)'
    clonedArticle.name_en = clonedArticle.name_en + ' (inclusive)'
  }

  basket.value.addItemForBoothPrint(clonedArticle)
}

const removeConfiguration = (configuration) => {
  basket.value.removeBoothConfiguration(configuration)
  basket.value.removeItemForBoothPrint(configuration.booth_refinement.id)
}

const confirmRemoveDraft = () => {
  Swal.fire({
    title: t('configurator.notices.titles.remove_booth_draft'),
    text: t('configurator.notices.message.remove_booth_draft'),
    showCancelButton: true,
    confirmButtonText: t('configurator.buttons.yes'),
    cancelButtonText: t('configurator.buttons.no'),
  })
    .then((result) => {
      if (result.isConfirmed) {
        removeDraft()
      }
    })
}

const removeDraft = async () => {
  showLoadingIndicator(t('configurator.indicators.remove_booth_draft'))
  await axios.delete('/print/' + cloud.value.uuid + '/booth-draft/' + boothDraft.value.identifier)
    .then((response) => {
      store.tmp.booth_draft = response.data.booth_draft
      showSuccessToast(t('configurator.toasts.messages.booth_draft_deleted'), t('toast.title.success'))
    })
    .catch((err) => {
      console.error(err)
      showErrorToast(t('configurator.toasts.errors.booth_draft_deleted'), t('toast.title.error'))
    })
}

const showUploadModal = () => {
  uploadModalActive.value = true
}

const hideUploadModal = () => {
  uploadModalActive.value = false
}

const onUploaded = async (file) => {
  await axios.post('/print/' + cloud.value.uuid + '/booth-draft', file)
    .then((response) => {
      store.tmp.booth_draft = response.data.booth_draft
      hideUploadModal()
    })
    .catch((err) => {
      console.error(err)
    })
}

const downloadBoothDraft = async () => {
  await downloadFile(
    '/print/' + cloud.value.uuid + '/booth-draft/' + boothDraft.value.identifier + '/download',
    boothDraft.value.original_name
  )
}
</script>